// src/pages/nfl/NflProjectionsShowdown.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
/* ------------------------------------------------------------------
   Showdown table
   - DK/FD site toggle + Flex vs CPT/MVP with 1.5x handling
   - Search, sort, CSV export
   - PNG logos with fallback
   - Selected slate controls every player, salary and upload ID
  - Shared forecasts from classic playersimulations.json
  - Defaults to the nearest upcoming game and advances at kickoff
   - Frozen first column + auto-fit widths
   - Optional heatmap palette (theme-aware)
   - Dark / Light theme, gradient card, header like classic projections
   - Floor/Ceiling columns
   - Point distribution modal (EXACT classic style) using Recharts
   - FIX: Ownership % values that come as fractions (0-1) now display correctly
\------------------------------------------------------------------- */
const SIM_SRC = "/data/nfl/classic/latest/playersimulations.json";
const VEGAS_SRC = "/data/nfl/classic/latest/vegas.json";
const PROJ_SRC = "/data/nfl/showdown/latest/projections.json";
const IDS_SRC = "/data/nfl/showdown/latest/site_ids.json";
const META_SRC = "/data/nfl/latest/meta.json";
const OWNERSHIP_META_SRC = "/data/nfl/showdown/latest/meta.json";
const POINT_DISTS_SRC = "/data/nfl/latest/point_distributions.json";
/* Full-team names for DST display */
const TEAM_FULL = {
  ARI: "Arizona Cardinals",
  ATL: "Atlanta Falcons",
  BAL: "Baltimore Ravens",
  BUF: "Buffalo Bills",
  CAR: "Carolina Panthers",
  CHI: "Chicago Bears",
  CIN: "Cincinnati Bengals",
  CLE: "Cleveland Browns",
  DAL: "Dallas Cowboys",
  DEN: "Denver Broncos",
  DET: "Detroit Lions",
  GB: "Green Bay Packers",
  HOU: "Houston Texans",
  IND: "Indianapolis Colts",
  JAX: "Jacksonville Jaguars",
  KC: "Kansas City Chiefs",
  LV: "Las Vegas Raiders",
  LAC: "Los Angeles Chargers",
  LAR: "Los Angeles Rams",
  MIA: "Miami Dolphins",
  MIN: "Minnesota Vikings",
  NE: "New England Patriots",
  NO: "New Orleans Saints",
  NYG: "New York Giants",
  NYJ: "New York Jets",
  PHI: "Philadelphia Eagles",
  PIT: "Pittsburgh Steelers",
  SEA: "Seattle Seahawks",
  SF: "San Francisco 49ers",
  TB: "Tampa Bay Buccaneers",
  TEN: "Tennessee Titans",
  WAS: "Washington Commanders",
};
/* ---------- tiny helpers ---------- */
const toNum = (v) => {
  if (v == null || String(v).trim() === "") return null;
  const n = Number(String(v ?? "").replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const fmt0 = (v) => (toNum(v) == null ? "" : toNum(v).toLocaleString());
const fmt1 = (v) => (toNum(v) == null ? "" : toNum(v).toFixed(1));
/**
 * FIX: ownership can arrive as:
 * - fraction: 0.531 (meaning 53.1%)
 * - percent: 53.1 or "53.1%"
 * We normalize display to always show percent with 1 decimal.
 */
const fmtPctSmart = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (/%$/.test(s)) {
    const n = toNum(s);
    return n == null ? "" : `${n.toFixed(1)}%`;
  }
  const n = toNum(s);
  if (n == null) return "";
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toFixed(1)}%`;
};
// keep old name usage in code paths that call fmtPct
const fmtPct = fmtPctSmart;
const pick = (obj, keys, fallback = "") => {
  for (const k of keys) if (k in obj && obj[k] !== "" && obj[k] != null) return obj[k];
  return fallback;
};
const computeVal = (proj, sal) => {
  const p = toNum(proj);
  const s = toNum(sal);
  if (p == null || s == null || s === 0) return "";
  return (p / (s / 1000)).toFixed(1); // points per $1k
};
const isDSTPos = (pos) => ["DST", "D/ST", "DEF", "DEFENSE", "D"].includes(String(pos || "").trim().toUpperCase());
/* PNG logos */
const logoSrc = (abbr) => `/logos/nfl/${String(abbr || "").toUpperCase()}.png`;
const logoSrcAlt = (abbr) => `/logos/${String(abbr || "").toUpperCase()}.png`;
/* -------- data fetchers -------- */
function useJson(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, loading, err };
}
/* -------- last updated like classic pages -------- */
function useLastUpdated(dataUrl, metaUrl) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    const setValidDate = (raw) => {
      if (!raw) return false;
      const dt = new Date(raw);
      if (Number.isNaN(dt.getTime())) return false;
      if (alive) setD(dt);
      return true;
    };
    (async () => {
      for (const method of ["HEAD", "GET"]) {
        try {
          const response = await fetch(`${dataUrl}?_=${Date.now()}`, {
            method, cache: "no-store",
          });
          if (response.ok && setValidDate(response.headers.get("last-modified"))) return;
        } catch {}
      }
      try {
        const response = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" });
        if (!response.ok) return;
        const m = await response.json();
        const art = Array.isArray(m?.artifacts)
          ? m.artifacts.find((x) =>
              String(x.path || "").replace(/^\//, "").endsWith(dataUrl.replace(/^\//, ""))
            )
          : null;
        const raw = art?.last_updated || art?.tags?.last_updated ||
          m?.last_updated || m?.updated_iso || m?.updated_utc || m?.updated;
        if (setValidDate(raw)) return;
        if (m?.updated_epoch) setValidDate(Number(m.updated_epoch) * 1000);
      } catch (e) {
        console.error("Showdown lastUpdated failed:", e);
      }
    })();
    return () => { alive = false; };
  }, [dataUrl, metaUrl]);
  return d;
}
const fmtUpdated = (d) =>
  d
    ? d.toLocaleString("en-US", {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
/* -------- CSV -------- */
const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};
function downloadCSV(rows, cols, fname = "nfl_showdown_projections.csv") {
  const header = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          let val = r[c.key];
          if (c.type === "money") val = fmt0(val);
          if (c.type === "num1") val = fmt1(val);
          if (c.type === "pct") val = fmtPct(val);
          return escapeCSV(val ?? "");
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
/* ---- heat stats helper: which columns get colored and dir ---- */
const dirForKey = (k) => {
  const id = String(k).toLowerCase();
  if (id === "sal") return null;
  if (id === "pown") return "lower";
  if (id === "opt" || id === "proj" || id === "val" || id === "floor" || id === "ceiling")
    return "higher";
  return null;
};
/* theme-aware palette like classic projections */
function heatTextColor(min, max, v, dir, theme = "dark") {
  if (v == null || min == null || max == null || min === max || !dir) return null;
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  const isDark = theme === "dark";
  let hue;
  let sat;
  let light;
  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5;
      hue = 0 + u * 60;
      sat = 80;
      light = 62 + u * 10;
    } else {
      const u = (t - 0.5) / 0.5;
      hue = 60 + u * 60;
      sat = 75;
      light = 72 - u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;
      hue = 0 + u * 60;
      sat = 85;
      light = 42 + u * 6;
    } else {
      const u = (t - 0.5) / 0.5;
      hue = 60 + u * 60;
      sat = 80;
      light = 38 - u * 4;
    }
  }
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}
const keynorm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
const scaleBins = (bins, mult = 1) => {
  if (!Array.isArray(bins)) return [];
  const m = Number(mult);
  if (!Number.isFinite(m) || m === 1) return bins;
  return bins.map((b) => ({
    ...b,
    bin_start: Number.isFinite(Number(b?.bin_start)) ? Number(b.bin_start) * m : b?.bin_start,
    bin_end: Number.isFinite(Number(b?.bin_end)) ? Number(b.bin_end) * m : b?.bin_end,
  }));
};
/* ======================= POINT DIST (classic behavior/style) ======================= */
/**
 * Goal: mirror Classic Slate modal behavior as closely as possible:
 * - Always \\\\\~20–22 bars (so labels never explode on MVP 1.5x ranges)
 * - Clean X-axis labeling (skip ticks automatically, rotate only if still dense)
 * - Same grid / bar styling, dashed proj line
 *
 * Input bins can be either:
 *  - your precomputed bins [{bin_start, bin_end, prob}]
 *  - OR we can build them from Floor/Ceil (normal approx) with a CLASSIC bin cap
 */
// erf approximation (good enough for UI bins)
function erf(x) {
  const sign = x < 0 ? -1 : 1;
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const ax = Math.abs(x);
  const t = 1 / (1 + p * ax);
  const y =
    1 -
    (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) * Math.exp(-ax * ax);
  return sign * y;
}
function normalCDF(x, mean, std) {
  if (!std || std <= 0) return x < mean ? 0 : 1;
  return 0.5 * (1 + erf((x - mean) / (std * Math.SQRT2)));
}
/ Snap to "nice" bin widths (classic feel) */
function niceEvenWidth(w) {
  // prefer 2, 4, 5, 6, 8, 10
  const candidates = [2, 4, 5, 6, 8, 10, 12];
  let best = candidates[0];
  let bestDiff = Infinity;
  for (const c of candidates) {
    const d = Math.abs(c - w);
    if (d < bestDiff) {
      bestDiff = d;
      best = c;
    }
  }
  return best;
}
// Build bins from Floor/Ceil (normal approx) with CLASSIC bin cap (\\\\\~22 bars)
function buildBinsFromFloorCeil(mean, floor, ceiling) {
  const m = toNum(mean);
  const f = toNum(floor);
  const c = toNum(ceiling);
  if (m == null || f == null || c == null || c <= f) return [];
  // same assumption you used before
  const std = (c - f) / 4;
  // cover a bit beyond floor/ceil (classic look)
  const minX = Math.max(0, Math.floor(f - std * 1.5));
  const maxX = Math.ceil(c + std * 1.5);
  // ✅ CLASSIC: cap bins so x-axis doesn't become a mess (especially MVP)
  const TARGET_BINS = 22;
  const span = Math.max(1, maxX - minX);
  const rawW = span / TARGET_BINS;
  // classic usually uses 2pt bins on tight spreads, wider bins on big spreads
  const width = niceEvenWidth(Math.max(2, Math.round(rawW)));
  const bins = [];
  for (let a = minX; a < maxX; a += width) {
    const b = Math.min(a + width, maxX);
    const p = normalCDF(b, m, std) - normalCDF(a, m, std);
    bins.push({
      bin_start: a,
      bin_end: b,
      prob: Math.max(0, p),
    });
  }
  // normalize to sum=1 (classic expects prob)
  const sum = bins.reduce((acc, x) => acc + (Number(x.prob) || 0), 0);
  if (sum > 0) for (const x of bins) x.prob = x.prob / sum;
  return bins;
}
function PointDistModal({ open, onClose, isDark, site, playerName, bins, proj }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsMobile(window.innerWidth < 640);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const chartData = Array.isArray(bins)
    ? bins.map((b, idx) => {
        const a = Number(b?.bin_start);
        const z = Number(b?.bin_end);
        const p = Number(b?.prob);
        const label =
          Number.isFinite(a) && Number.isFinite(z) ? `${a}-${z}` : `bin-${idx + 1}`;
        return {
          label,
          x: Number.isFinite(a) && Number.isFinite(z) ? (a + z) / 2 : idx,
          bin_start: a,
          bin_end: z,
          probPct: Number.isFinite(p) ? p * 100 : 0,
        };
      })
    : [];
  const hasData = chartData.length > 0;
  const xTickEvery = isMobile
    ? Math.max(1, Math.ceil(chartData.length / 5))
    : Math.max(1, Math.ceil(chartData.length / 10));
  const xTicks = chartData
    .filter((_, idx) => idx % xTickEvery === 0)
    .map((d) => d.x);
  const xDomain = useMemo(() => {
    const mids = chartData
      .map((d) => d.x)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    if (!mids.length) return [0, 1];
    if (mids.length === 1) return [mids[0] - 2.5, mids[0] + 2.5];
    const firstGap = Math.abs(mids[1] - mids[0]) || 1;
    const lastGap =
      Math.abs(mids[mids.length - 1] - mids[mids.length - 2]) || firstGap;
    return [
      mids[0] - firstGap / 2,
      mids[mids.length - 1] + lastGap / 2,
    ];
  }, [chartData]);
  // Keep all hooks above the conditional return so hook order never changes.
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className={[
          "relative w-full max-w-3xl max-h-[95vh] rounded-2xl border shadow-2xl overflow-y-auto",
          isDark
            ? "border-slate-700/60 bg-slate-950 text-slate-100"
            : "border-slate-200 bg-white text-slate-900",
        ].join(" ")}
      >
        <div className={isDark ? "bg-slate-950/95" : "bg-white"}>
          <div className="flex items-start justify-between gap-3 px-3 pt-3 sm:gap-4 sm:px-5 sm:pt-5">
            <div className="min-w-0">
              <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                Fantasy Points Distribution
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="max-w-[190px] truncate text-base font-extrabold tracking-tight sm:max-w-none sm:text-lg">
                  {playerName || "—"}
                </div>
                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide",
                    site === "DK"
                      ? isDark
                        ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                        : "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : isDark
                      ? "bg-sky-500/15 text-sky-200 border border-sky-500/30"
                      : "bg-sky-50 text-sky-800 border border-sky-200",
                  ].join(" ")}
                >
                  {site}
                </span>
                {Number.isFinite(proj) ? (
                  <span className={isDark ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                    Proj: <span className="font-semibold tabular-nums">{proj.toFixed(1)}</span>
                  </span>
                ) : null}
              </div>
            </div>
            <button
              className={[
                "shrink-0 rounded-xl border px-2.5 py-1.5 text-xs font-semibold sm:px-3 sm:py-2 sm:text-sm",
                isDark
                  ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900"
                  : "border-slate-200 bg-slate-50 hover:bg-slate-100",
              ].join(" ")}
              onClick={onClose}
            >
              Close
            </button>
          </div>
          <div className="px-2 pb-3 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
            {!hasData ? (
              <div className={isDark ? "text-sm text-slate-300" : "text-sm text-slate-700"}>
                No distribution data found for this player yet.
              </div>
            ) : (
              <div className={isMobile ? "h-[360px] w-full" : "h-[320px] w-full"}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 18,
                      left: 8,
                      bottom: isMobile ? 42 : 18,
                    }}
                    barCategoryGap={isMobile ? "12%" : "8%"}
                  >
                    <CartesianGrid
                      stroke={isDark ? "#334155" : "#e5e7eb"}
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={xDomain}
                      ticks={xTicks}
                      tickFormatter={(v) =>
                        Number.isInteger(Number(v))
                          ? String(Number(v))
                          : Number(v).toFixed(1)
                      }
                      height={isMobile ? 52 : 30}
                      angle={isMobile ? -35 : 0}
                      textAnchor={isMobile ? "end" : "middle"}
                      tickMargin={6}
                      tick={{
                        fontSize: isMobile ? 11 : 12,
                        fill: isDark ? "#cbd5f5" : "#334155",
                      }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{
                        fontSize: 11,
                        fill: isDark ? "#cbd5f5" : "#334155",
                      }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                      width={isMobile ? 38 : 60}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,189,248,0.12)" }}
                      contentStyle={{
                        backgroundColor: isDark ? "#020617" : "#ffffff",
                        border: "1px solid #38bdf8",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      labelStyle={{
                        color: isDark ? "#e2e8f0" : "#0f172a",
                      }}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, "Probability"]}
                      labelFormatter={(_, payload) => {
                        const d = payload?.[0]?.payload;
                        return d?.label ? `Bin ${d.label}` : "Bin";
                      }}
                    />
                    {Number.isFinite(proj) ? (
                      <ReferenceLine
                        x={proj}
                        stroke="#38bdf8"
                        strokeWidth={2}
                        strokeDasharray="6 4"
                      />
                    ) : null}
                    <Bar
                      dataKey="probPct"
                      radius={[6, 6, 0, 0]}
                      fill="#7dd3fc"
                      stroke="#38bdf8"
                      strokeWidth={1}
                      maxBarSize={42}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
const numberLike = value => toNum(value) ?? 0;
const SITES = { dk: { capTag: "CPT", flexFieldPrefix: "DK FLEX", mvpFieldPrefix: "DK CPT" }, fd: { capTag: "MVP", flexFieldPrefix: "FD FLEX", mvpFieldPrefix: "FD MVP" } };
const NFL_TEAMS = {
  ARI:{city:"Arizona",nickname:"Cardinals"}, ATL:{city:"Atlanta",nickname:"Falcons"},
  BAL:{city:"Baltimore",nickname:"Ravens"}, BUF:{city:"Buffalo",nickname:"Bills"},
  CAR:{city:"Carolina",nickname:"Panthers"}, CHI:{city:"Chicago",nickname:"Bears"},
  CIN:{city:"Cincinnati",nickname:"Bengals"}, CLE:{city:"Cleveland",nickname:"Browns"},
  DAL:{city:"Dallas",nickname:"Cowboys"}, DEN:{city:"Denver",nickname:"Broncos"},
  DET:{city:"Detroit",nickname:"Lions"}, GB:{city:"Green Bay",nickname:"Packers"},
  HOU:{city:"Houston",nickname:"Texans"}, IND:{city:"Indianapolis",nickname:"Colts"},
  JAX:{city:"Jacksonville",nickname:"Jaguars"}, KC:{city:"Kansas City",nickname:"Chiefs"},
  LAC:{city:"Los Angeles",nickname:"Chargers"}, LAR:{city:"Los Angeles",nickname:"Rams"},
  LV:{city:"Las Vegas",nickname:"Raiders"}, MIA:{city:"Miami",nickname:"Dolphins"},
  MIN:{city:"Minnesota",nickname:"Vikings"}, NE:{city:"New England",nickname:"Patriots"},
  NO:{city:"New Orleans",nickname:"Saints"}, NYG:{city:"New York",nickname:"Giants"},
  NYJ:{city:"New York",nickname:"Jets"}, PHI:{city:"Philadelphia",nickname:"Eagles"},
  PIT:{city:"Pittsburgh",nickname:"Steelers"}, SEA:{city:"Seattle",nickname:"Seahawks"},
  SF:{city:"San Francisco",nickname:"49ers"}, TB:{city:"Tampa Bay",nickname:"Buccaneers"},
  TEN:{city:"Tennessee",nickname:"Titans"}, WAS:{city:"Washington",nickname:"Commanders"},
};
function sdPick(obj, variants, fallback = 0) {
  for (const v of variants) {
    if (obj == null) break;
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase().replace(/[^a-z0-9]/g,"") === v.toLowerCase().replace(/[^a-z0-9]/g,"")) {
        return obj[k];
      }
    }
  }
  return fallback;
}
/* --------------------- name/pos/team normalization ----------------- */
function sdNormName(s) { return String(s || "").toLowerCase().normalize("NFKD")
 .replace(/[\u0300-\u036f]/g, "").replace(/[.'’,-]/g, " ").replace(/\s+(jr|sr|ii|iii|iv)\b/g, "")
 .replace(/\s+/g, " ").trim(); }
function normTeam(s){ const t = String(s || "").toUpperCase().trim(); return t === "JAC" ? "JAX" : t === "WSH" ? "WAS" : t; }
function normPosRaw(s){return (s||"").toUpperCase().trim();}
function sdNormPos(s){ const p = normPosRaw(s).replace(/\d+$/, ""); return ["D", "DEF", "DEFENSE", "D/ST", "DST"].includes(p) ? "DST" : p; }
function inferTeamFromNameForDST(name) {
  const nm = sdNormName(name);
  // Nicknames identify LA/NY defenses without ambiguous shared-city matches.
  for (const [abbr, team] of Object.entries(NFL_TEAMS)) {
    if (nm === sdNormName(team.nickname) || nm === sdNormName(`${team.city} ${team.nickname}`) || nm.includes(sdNormName(team.nickname))) return abbr;
  }
  const cities = Object.entries(NFL_TEAMS).filter(([, team]) => nm === sdNormName(team.city));
  return cities.length === 1 ? cities[0][0] : "";
}
// Shared data stays separate from site/slate-specific salary and upload IDs.
const rowsOf = (data) => Array.isArray(data) ? data : data?.rows ?? data?.players ?? data?.data ?? [];
function showdownPlayerKey(name, team, pos = "") {
  const tm = normTeam(team);
  const dst = sdNormPos(pos) === "DST" || (tm && inferTeamFromNameForDST(name) === tm);
  return `${tm}|${dst ? "DST" : sdNormName(name)}`;
}
function availableShowdownSlates(data, site) {
  // Old JSON remains usable while the replacement exporter is being installed.
  const source = Array.isArray(data?.slates) ? data.slates
    : data && (data.dk?.length || data.fd?.length) ? [{ ...data, key: "legacy", label: "Current showdown" }] : [];
  return source.filter((s) => Array.isArray(s[site]) && s[site].length > 0)
    .slice().sort((a, b) => String(a.gameDate || "").localeCompare(String(b.gameDate || "")) || a.key.localeCompare(b.key));
}
// Interpret salary-file wall-clock timestamps in Eastern time, regardless of
// the user's browser timezone, including Eastern standard/daylight time.
function showdownKickoffMillis(slate) {
  const raw = String(slate?.gameDate || "").trim();
  if (!raw) return NaN;
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return Date.parse(raw);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return NaN;
  const target = Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0));
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: slate.timezone || "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  });
  let instant = target;
  for (let attempt = 0; attempt < 3; attempt++) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map(({ type, value }) => [type, value]));
    const displayed = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    const correction = target - displayed;
    if (!correction) return instant;
    instant += correction;
  }
  return instant;
}
function chooseShowdownSlate(slates, selection, now) {
  const choices = slates.map((slate) => ({ slate, kickoff: showdownKickoffMillis(slate) }))
    .sort((a, b) => (Number.isFinite(a.kickoff) ? a.kickoff : Infinity) - (Number.isFinite(b.kickoff) ? b.kickoff : Infinity));
  const manual = selection && choices.find(({ slate }) => slate.key === selection.key);
  // A future game selected manually advances once it locks. Selecting a game
  // that was already locked deliberately opens its history for review.
  if (manual && (!Number.isFinite(manual.kickoff) || now < manual.kickoff || selection.selectedAt >= manual.kickoff)) return manual.slate;
  const upcoming = choices.find(({ kickoff }) => Number.isFinite(kickoff) && kickoff > now);
  if (upcoming) return upcoming.slate;
  // If every game has locked, keep the latest game available for review.
  return choices.filter(({ kickoff }) => Number.isFinite(kickoff)).at(-1)?.slate || choices[0]?.slate || null;
}
function buildShowdownRows({ slate, site, simulations, projections, legacyIds, customProj }) {
  if (!slate) return [];
  const scheduleTeams = [slate.away, slate.home].filter(Boolean).map(normTeam);
  const records = slate[site] || [];
  const groups = new Map();
  for (const rec of records) {
    const tm = normTeam(rec.team);
    const k = showdownPlayerKey(rec.name, tm, rec.player_position || rec.pos);
    const g = groups.get(k) || { name: rec.name, team: tm, records: [] };
    g.records.push(rec); groups.set(k, g);
  }
  const index = (data) => new Map(rowsOf(data).map((r) => [
    showdownPlayerKey(r.player ?? r.name ?? r.Player, r.team ?? r.Team, r.pos ?? r.Pos), r,
  ]));
  const simMap = index(simulations), projMap = index(projections);
  const originalKeys = new Set((legacyIds?.[site] || []).map((r) => r.slate_key).filter(Boolean));
  // The old projections workbook only supplies rates for its original game.
  const originalGame = slate.key === "legacy" || originalKeys.has(slate.key);
  const pct = (v) => {
    if (v == null || String(v).trim() === "") return null;
    const n = toNum(v);
      if (n == null) return null;
    return String(v).includes("%") || n > 1 ? n / 100 : n;
  };
  const output = [];
  for (const [k, group] of groups) {
    const sim = simMap.get(k), old = originalGame ? projMap.get(k) : null;
    const flex = site === "dk" ? group.records.find((r) => r.pos === "FLEX") : group.records[0];
    const cap = site === "dk" ? group.records.find((r) => r.pos === "CPT") : flex;
    if (!flex || !cap || !flex.id || !cap.id) continue;
    const salary = numberLike(site === "dk" ? flex.salary : flex.salary_flex);
    const capSalary = numberLike(site === "dk" ? cap.salary : cap.salary_mvp);
    if (salary <= 0 || capSalary <= 0) continue;
    const source = sim || old || {};
    const name = sim?.player ?? old?.player ?? old?.Player ?? group.name;
    const pos = sdNormPos(source.pos ?? source.Pos ?? flex.player_position ?? flex.pos);
    const salaryPos = sdNormPos(flex.player_position || flex.pos);
    const realPos = ["QB", "RB", "WR", "TE", "DST", "K"].includes(pos) ? pos : (k.endsWith("|DST") ? "DST" : salaryPos);
    const baseProj = toNum(sdPick(source, [`${site}_proj`, `${site} proj`, `${SITES[site].flexFieldPrefix} proj`], null));
    const floor = toNum(sdPick(source, [`${site}_floor`, `${SITES[site].flexFieldPrefix} floor`], null));
    const ceil = toNum(sdPick(source, [`${site}_ceiling`, `${site}_ceil`, `${SITES[site].flexFieldPrefix} ceiling`], null));
    const capTag = SITES[site].capTag;
    const slotRate = (field, isCap) => {
      if (!old) return null;
      const prefix = isCap ? SITES[site].mvpFieldPrefix : SITES[site].flexFieldPrefix;
      const tag = capTag.toLowerCase();
      const fields = isCap
        ? [`${site}_${tag}_${field}`, `${site}_${tag}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`]
        : [`${site}_${field}`, `${site}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`];
      return pct(sdPick(old, fields, null));
    };
    const customFlex = customProj?.[`${name}::FLEX`];
    const customCap = customProj?.[`${name}::${capTag}`];
    const hasFlexCustom = customFlex != null && String(customFlex).trim() !== "";
    const hasCapCustom = customCap != null && String(customCap).trim() !== "";
    // Captain/MVP receives 1.5x points. Salaries always come from the salary file.
    const baseCapProj = baseProj == null ? null : baseProj * 1.5;
    output.push({
      name, team: group.team, pos: realPos,
      opp: scheduleTeams.find((tm) => tm !== group.team) || source.opp || source.Opp || "",
      salary, cap_salary: capSalary,
      proj: hasFlexCustom ? Math.max(0, numberLike(customFlex)) : baseProj,
      cap_proj: hasCapCustom ? Math.max(0, numberLike(customCap)) : baseCapProj,
      base_proj: baseProj, base_cap_proj: baseCapProj,
      custom_proj: hasFlexCustom ? customFlex : "", custom_cap_proj: hasCapCustom ? customCap : "",
      has_custom_proj: hasFlexCustom, has_custom_cap_proj: hasCapCustom,
      floor, ceil, cap_floor: floor == null ? null : floor * 1.5, cap_ceil: ceil == null ? null : ceil * 1.5,
      pown: slotRate("pown", false), opt: slotRate("opt", false),
      cap_pown: slotRate("pown", true), cap_opt: slotRate("opt", true),
      hasProjection: !!(sim || old) && baseProj > 0,
      siteIdOnly: !(sim || old) || baseProj <= 0,
      siteId: String(flex.id), capSiteId: String(cap.id),
      siteName: flex.name, capSiteName: cap.name,
      fdPrefix: slate.fd_prefix || flex.prefix || null,
      slateKey: slate.key, __raw: source,
    });
  }
  return output;
}
/* ------------------------------- page ------------------------------- */
export default function NflProjectionsShowdown() {
  const { data: simulations, loading: simsLoading, err: simsErr } = useJson(SIM_SRC);
  const { data: projRaw } = useJson(PROJ_SRC);
  const { data: vegasRaw } = useJson(VEGAS_SRC);
  const { data: idsRaw, loading: idsLoading, err: idsErr } = useJson(IDS_SRC);
  const { data: pointDistsRaw } = useJson(POINT_DISTS_SRC);
  const projectionsUpdatedAt = useLastUpdated(SIM_SRC, META_SRC);
  const ownershipUpdatedAt = useLastUpdated(PROJ_SRC, OWNERSHIP_META_SRC);
  const [site, setSite] = useState("dk"); // default DK
  const [slot, setSlot] = useState("flex");
  const [slateSelection, setSlateSelection] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const availableSlates = useMemo(() => availableShowdownSlates(idsRaw, site), [idsRaw, site]);
  const activeSlate = chooseShowdownSlate(availableSlates, slateSelection?.site === site ? slateSelection : null, now);
  const slateKey = activeSlate?.key || "none";
  const rawRows = useMemo(() => buildShowdownRows({ slate: activeSlate, site, simulations, projections: projRaw, legacyIds: idsRaw, customProj: {} }), [activeSlate, site, simulations, projRaw, idsRaw]);
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("dark"); // "dark" | "light"
  // ✅ classic-style dist modal state
  const [pointDistOpen, setPointDistOpen] = useState(false);
  const [pointDistSite, setPointDistSite] = useState("DK"); // "DK" | "FD"
  const [pointDistPlayerName, setPointDistPlayerName] = useState("");
  const [pointDistBins, setPointDistBins] = useState([]);
  const [pointDistProj, setPointDistProj] = useState(null);
  useEffect(() => { setPointDistOpen(false); }, [slateKey]);
  const isDark = theme === "dark";
  const pointDistsIndex = useMemo(() => {
    const pd = pointDistsRaw && typeof pointDistsRaw === "object" ? pointDistsRaw : null;
    if (!pd) return { dk: {}, fd: {} };
    const pickSite = (obj) => {
      const out = {};
      if (!obj || typeof obj !== "object") return out;
      const add = (key, entry) => {
        const k = keynorm(key);
        const bins = Array.isArray(entry)
          ? entry
          : Array.isArray(entry?.bins)
          ? entry.bins
          : null;
        if (k && bins && !out[k]) out[k] = bins;
      };
      for (const [rawKey, entry] of Object.entries(obj)) {
        const parts = String(rawKey).split("|").map((x) => x.trim());
        const team = entry?.team || (parts.length >= 2 ? parts[0] : "");
        const player = entry?.player || (parts.length >= 2 ? parts[1] : rawKey);
        const slotName = entry?.slot || (parts.length >= 3 ? parts[2] : "");
        add(player, entry);
        add(`${team} ${player}`, entry);
        add(`${team}|${player}|${slotName}`, entry);
        add(rawKey, entry);
      }
      return out;
    };
    return {
      dk: pickSite(pd.dk ?? pd.DK ?? {}),
      fd: pickSite(pd.fd ?? pd.FD ?? {}),
    };
  }, [pointDistsRaw]);
  const rows = useMemo(() => rawRows.map((r) => {
    const game = rowsOf(vegasRaw).find((game) => normTeam(game.team ?? game.Team) === r.team);
    const player = isDSTPos(r.pos) ? site === "dk" ? TEAM_FULL[r.team]?.split(" ").slice(-1)[0] || r.name : TEAM_FULL[r.team] || r.name : r.name;
    return { ...r, player, projectionName: r.name, opp: normTeam(r.opp || game?.opponent || game?.opp),
      flex_proj: r.proj, flex_floor: r.floor, flex_ceiling: r.ceil };
  }), [rawRows, site, vegasRaw]);
  // search
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (!(toNum(r.flex_proj) > 0)) return false;
      return !s || `${r.player} ${r.pos} ${r.team} ${r.opp}`
        .toLowerCase()
        .includes(s);
    });
  }, [rows, q]);
  // Salary and upload ID always belong to this slate and roster role.
  const modeRows = useMemo(() => filtered.map((r) => {
    const cap = slot !== "flex";
    const sal = cap ? r.cap_salary : r.salary;
    const proj = cap ? r.cap_proj : r.flex_proj;
    const floor = cap ? r.cap_floor : r.flex_floor;
    const ceiling = cap ? r.cap_ceil : r.flex_ceiling;
    return { ...r, sal, proj, floor, ceiling, val: computeVal(proj, sal),
      pOwn: cap ? r.cap_pown : r.pown, opt: cap ? r.cap_opt : r.opt,
      uploadId: cap ? r.capSiteId : r.siteId };
  }), [filtered, slot]);
  // columns with width hints (auto-fit)
  const columns = useMemo(() => {
    const isDK = site === "dk";
    const sal = isDK
      ? slot === "flex"
        ? "DK Sal"
        : "DK CPT Sal"
      : slot === "flex"
      ? "FD Sal"
      : "FD MVP Sal";
    const proj = isDK
      ? slot === "flex"
        ? "DK Proj"
        : "DK CPT Proj"
      : slot === "flex"
      ? "FD Proj"
      : "FD MVP Proj";
    const pown = isDK
      ? slot === "flex"
        ? "DK Flex pOWN%"
        : "DK CPT pOWN%"
      : slot === "flex"
      ? "FD Flex pOWN%"
      : "FD MVP pOWN%";
    const opt = isDK
      ? slot === "flex"
        ? "DK Flex Opt%"
        : "DK CPT Opt%"
      : slot === "flex"
      ? "FD Flex Opt%"
      : "FD MVP Opt%";
    return [
      { key: "player", label: "Player", type: "text", thClass: "w-[170px]", tdClass: "w-[170px] max-w-[170px]" },
      { key: "pos", label: "Pos", type: "text", thClass: "w-[52px]", tdClass: "w-[52px]" },
      { key: "team", label: "Team", type: "text", thClass: "w-[64px]", tdClass: "w-[64px]" },
      { key: "opp", label: "Opp", type: "text", thClass: "w-[64px]", tdClass: "w-[64px]" },
      { key: "sal", label: sal, type: "money", thClass: "w-[92px]", tdClass: "w-[92px]" },
      { key: "proj", label: proj, type: "num1", thClass: "w-[72px]", tdClass: "w-[72px]" },
      { key: "floor", label: isDK ? "DK Floor" : "FD Floor", type: "num1", thClass: "w-[74px]", tdClass: "w-[74px]" },
      { key: "ceiling", label: isDK ? "DK Ceiling" : "FD Ceiling", type: "num1", thClass: "w-[86px]", tdClass: "w-[86px]" },
      { key: "val", label: isDK ? "DK Val" : "FD Val", type: "num1", thClass: "w-[72px]", tdClass: "w-[72px]" },
      { key: "pOwn", label: pown, type: "pct", thClass: "w-[92px]", tdClass: "w-[92px]" },
      { key: "opt", label: opt, type: "pct", thClass: "w-[84px]", tdClass: "w-[84px]" },
    ];
  }, [site, slot]);
  // sort
  const [sort, setSort] = useState({ key: "proj", dir: "desc" });
  useEffect(() => {
    setSort({ key: "proj", dir: "desc" });
  }, [site, slot, slateKey]);
  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const arr = [...modeRows];
    const typeFor = (k) => columns.find((c) => c.key === k)?.type;
    arr.sort((a, b) => {
      const t = typeFor(sort.key);
      const av = t === "text" ? String(a[sort.key] ?? "").toLowerCase() : toNum(a[sort.key]);
      const bv = t === "text" ? String(b[sort.key] ?? "").toLowerCase() : toNum(b[sort.key]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (t === "text") return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
      return (av - bv) * dir;
    });
    return arr;
  }, [modeRows, sort, columns]);
  const onSort = (col) => {
    setSort((prev) => {
      if (prev.key !== col.key) return { key: col.key, dir: "desc" };
      return { key: col.key, dir: prev.dir === "desc" ? "asc" : "desc" };
    });
  };
  /* ---- heat stats over visible rows (for optional palette) ---- */
  const heatStats = useMemo(() => {
    const stats = {};
    if (!sorted.length) return stats;
    for (const c of columns) {
      const k = c.key;
      const dir = dirForKey(k);
      if (!dir) continue;
      let min = Infinity, max = -Infinity;
      for (const r of sorted) {
        const raw = c.type === "pct" ? String(r[c.key]).replace("%", "") : r[c.key];
        const v = toNum(raw);
        if (v == null) continue;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (min !== Infinity && max !== -Infinity) stats[k] = { min, max, dir };
    }
    return stats;
  }, [sorted, columns]);
  /* ---- UI classes (borrowed style from classic projections) ---- */
  const textSz = "text-[11px] md:text-[12px]";
  const cell = "px-2 py-1 text-center";
  const headerBase = "px-2 py-1 font-semibold text-center";
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const headerBgClass = isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-700";
  const headerBgCell = isDark ? "bg-slate-900" : "bg-slate-50";
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";
  const tableTextClass = isDark ? "text-slate-100" : "text-slate-900";
  const loading = simsLoading || idsLoading;
  const err = simsErr || idsErr;
  const openPointDist = (row) => {
    const siteLabel = site === "dk" ? "DK" : "FD";
    const siteKey = site === "dk" ? "dk" : "fd";
    const p = toNum(row?.proj);
    const mult = slot === "flex" ? 1 : 1.5;
    const distMap = pointDistsIndex?.[siteKey] ?? {};
    const candidates = [
      `${row?.team || ""} ${row?.projectionName || row?.player || ""}`,
      row?.projectionName,
      row?.player,
      `${row?.team || ""} ${row?.player || ""}`,
      `${row?.team || ""}|${row?.player || ""}|${row?.pos || ""}`,
    ];
    const rawBins = candidates
      .map((x) => distMap[keynorm(x)])
      .find((x) => Array.isArray(x) && x.length > 0);
    const bins = rawBins
      ? scaleBins(rawBins, mult)
      : buildBinsFromFloorCeil(row?.proj, row?.floor, row?.ceiling);
    setPointDistSite(siteLabel);
    setPointDistPlayerName(row?.player || "");
    setPointDistBins(bins);
    setPointDistProj(Number.isFinite(p) ? p : null);
    setPointDistOpen(true);
  };
  return (
    <div className={outerClass}>
      {/* ✅ classic-style distribution modal */}
      <PointDistModal
        open={pointDistOpen}
        onClose={() => setPointDistOpen(false)}
        isDark={isDark}
        site={pointDistSite}
        playerName={pointDistPlayerName}
        bins={pointDistBins}
        proj={pointDistProj}
      />
      <div className="w-full px-4 md:px-6 py-5">
        {/* Header bar */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">NFL — DFS Showdown Projections</h1>
            <div className={"flex flex-wrap gap-x-3 gap-y-1 text-[11px] italic " + (isDark ? "text-slate-400" : "text-slate-500")}>
              <span>Projections updated: {fmtUpdated(projectionsUpdatedAt)}</span>
              <span>Ownership updated: {fmtUpdated(ownershipUpdatedAt)}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </div>
        <div className={cardClass + " mb-4"}>
          <div className="flex flex-wrap items-end gap-x-5 gap-y-3 px-4 py-3">
            <div>
              <div className={"mb-1 text-[11px] font-semibold " + (isDark ? "text-slate-400" : "text-slate-500")}>Site</div>
            {/* Site toggle */}
            <div className={"inline-flex items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {["dk", "fd"].map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    setSite(k);
                    setSlot("flex");
                    setPointDistOpen(false);
                  }}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                    site === k ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                  }`}
                >
                  <img src={k === "dk" ? "/logos/dk.png" : "/logos/fd.png"} alt={k.toUpperCase()} className="w-4 h-4" />
                  <span>{k.toUpperCase()}</span>
                </button>
              ))}
            </div>
            </div>
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-[11px] font-semibold sm:max-w-md">
              <span className={isDark ? "text-slate-400" : "text-slate-500"}>Slate</span>
            <select aria-label="Showdown slate" value={slateKey} disabled={!availableSlates.length}
              onChange={(event) => { setSlateSelection({ site, key: event.target.value, selectedAt: Date.now() }); setPointDistOpen(false); }}
              className={"h-8 w-full min-w-0 max-w-full rounded-md border px-2 text-xs font-normal " + (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300")}>
              {!availableSlates.length && <option value="none">No showdown slates available</option>}
              {availableSlates.map((slate) => <option key={slate.key} value={slate.key}>{slate.label || slate.key}</option>)}
            </select>
            </label>
          </div>
        </div>
        <div className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <div>
              <h2 className="text-sm font-semibold">Showdown Projections <span className={"ml-2 text-xs font-normal " + (isDark ? "text-slate-400" : "text-slate-500")}>{!loading && !err ? `${sorted.length.toLocaleString()} players` : ""}</span></h2>
              <p className={"mt-1 text-[11px] " + (isDark ? "text-slate-400" : "text-slate-500")}>Click a player for their point distribution.</p>
            </div>
            {/* export */}
            <button
              className="h-8 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400"
              onClick={() => downloadCSV(sorted, columns, `nfl_showdown_${slateKey}_${site}_${slot}.csv`)}
            >
              Export CSV
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            {/* Slot toggle */}
            <div className={"inline-flex items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {site === "dk" ? (
                <>
                  <button
                    className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                      slot === "flex" ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                    }`}
                    onClick={() => {
                      setSlot("flex");
                      setPointDistOpen(false);
                    }}
                  >
                    DK Flex
                  </button>
                  <button
                    className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                      slot === "cpt" ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                    }`}
                    onClick={() => {
                      setSlot("cpt");
                      setPointDistOpen(false);
                    }}
                  >
                    DK CPT
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                      slot === "flex" ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                    }`}
                    onClick={() => {
                      setSlot("flex");
                      setPointDistOpen(false);
                    }}
                  >
                    FD Flex
                  </button>
                  <button
                    className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                      slot === "mvp" ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                    }`}
                    onClick={() => {
                      setSlot("mvp");
                      setPointDistOpen(false);
                    }}
                  >
                    FD MVP
                  </button>
                </>
              )}
            </div>
            {/* search */}
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search player / team / opp…"
              className={
                "h-8 w-full sm:w-64 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
              }
            />
          </div>
          {/* table */}
          <div className="overflow-auto h-[70vh] rounded-b-2xl">
            <table className={`border-separate table-auto w-max min-w-full ${textSz} ${tableTextClass}`} style={{ borderSpacing: 0 }}>
              <thead className={`${headerBgClass} sticky top-0 z-20 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}>
                <tr>
                  {columns.map((c, idx) => (
                    <th
                      key={c.key}
                      className={`${headerBase} whitespace-nowrap cursor-pointer select-none ${c.thClass || ""} ${headerBgCell} ${
                        idx === 0 ? "sticky left-0 z-30" : ""
                      }`}
                      onClick={() => onSort(c)}
                      title="Click to sort"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>{c.label}</span>
                        {sort.key === c.key ? (
                          <span className="text-[10px] text-slate-400">{sort.dir === "desc" ? "▼" : "▲"}</span>
                        ) : (
                          <span className="text-[9px] text-slate-600">▲</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td className={`${cell} text-slate-400`} colSpan={columns.length}>
                      Loading…
                    </td>
                  </tr>
                )}
                {err && !loading && (
                  <tr>
                    <td className={`${cell} text-red-400`} colSpan={columns.length}>
                      Failed to load: {err}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !err &&
                  sorted.map((r, i) => {
                    const rowBg = i % 2 === 0 ? rowOddClass : rowEvenClass;
                    const rowKey = `${r.player}-${r.team}-${r.pos}-${i}`;
                    return (
                      <tr
                        key={rowKey}
                        className={rowBg + " cursor-pointer"}
                        onClick={() => openPointDist(r)}
                        title="Click for distribution (classic)"
                      >
                        {columns.map((c, idx) => {
                          if (c.key === "player") {
                            return (
                              <td
                                key={c.key}
                                className={`px-2 py-1 text-left sticky left-0 z-10 ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)] w-[170px] max-w-[170px]`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <img
                                    src={logoSrc(r.team)}
                                    alt={r.team}
                                    className="w-4 h-4 rounded-sm object-contain flex-none"
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = logoSrcAlt(r.team);
                                    }}
                                  />
                                  <span className="truncate">{r.player}</span>
                                </div>
                              </td>
                            );
                          }
                          const stat = heatStats[c.key];
                          const rawForRange =
                            c.type === "pct"
                              ? String(r[c.key] ?? "").replace("%", "")
                              : r[c.key];
                          const vNum = toNum(rawForRange);
                          let textColor = null;
                          const hasValue =
                            rawForRange !== "" &&
                            rawForRange !== null &&
                            rawForRange !== undefined;
                          if (stat && hasValue && vNum != null) {
                            textColor = heatTextColor(stat.min, stat.max, vNum, stat.dir, theme);
                          }
                          let content = r[c.key];
                          if (c.type === "money") content = fmt0(content);
                          if (c.type === "num1") content = fmt1(content);
                          if (c.type === "pct") content = fmtPct(content);
                          const align = c.key === "player" ? "text-left" : "text-center";
                          return (
                            <td
                              key={c.key}
                              className={`${c.tdClass || ""} ${c.type === "text" ? "px-2" : ""} ${align} py-1 whitespace-nowrap ${
                                c.type !== "text" ? "tabular-nums px-1.5" : ""
                              }`}
                              style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                              title={String(content ?? "")}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                {!loading && !err && !sorted.length && (
                  <tr>
                    <td className={`${cell} text-slate-400`} colSpan={columns.length}>
                      No data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}